import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronRight } from "lucide-react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/base/table";
import { useStateMachine } from "@/core/arch/state_machine";
import { ToolStatusBloc } from "@/application/tools/tool_status_bloc";
import { jobHistoryRepository } from "@/application/tools/job_history_repository";
import { useHasGroup } from "@/hooks/useHasGroup";
import { toolHistoryPath } from "@/routes";
import { TOOLS, type ToolDefinition } from "./tools";
import { RunOneClickToolSheet } from "./components/RunOneClickToolSheet";
import { DevelopersOnlyCard } from "./components/DevelopersOnlyCard";
import { JobStatusBadge } from "./components/JobStatusBadge";
import { formatRelative } from "./components/job_display";
import type { JobHistoryRow } from "@/application/tools/job_history_repository";

/** A tool that runs as a background job, so it has a run history to open. */
type JobTool = ToolDefinition & { jobType: string };

function isJobTool(tool: ToolDefinition): tool is JobTool {
  return tool.jobType != null;
}

const JOB_TOOLS = TOOLS.filter(isJobTool);
const ONE_TIME_TOOLS = TOOLS.filter((tool) => !isJobTool(tool));

/** Job types the last-run column has to fetch, deduped across tools. */
const JOB_TYPES = [...new Set(JOB_TOOLS.map((tool) => tool.jobType))];

function LastRun({
  latest,
  loaded,
}: {
  latest: JobHistoryRow | null;
  loaded: boolean;
}) {
  if (!loaded)
    return <span className="text-subtle-foreground text-xs">Loading…</span>;
  if (!latest)
    return <span className="text-subtle-foreground text-xs">Never run</span>;

  return (
    <div className="flex items-center gap-2">
      <JobStatusBadge status={latest.status} />
      <span className="text-muted-foreground text-xs">
        {formatRelative(latest.enqueuedAt, Date.now())}
      </span>
    </div>
  );
}

function Chevron() {
  return (
    <TableCell className="w-8 text-right align-top">
      <ChevronRight className="text-subtle-foreground inline h-4 w-4" />
    </TableCell>
  );
}

function SectionHeading({ title, hint }: { title: string; hint: string }) {
  return (
    <div className="mb-2">
      <h2 className="text-foreground text-sm font-semibold">{title}</h2>
      <p className="text-muted-foreground text-xs">{hint}</p>
    </div>
  );
}

export function ToolsView() {
  const [openToolId, setOpenToolId] = useState<string | null>(null);
  const navigate = useNavigate();
  const isDeveloper = useHasGroup("CPOS-Developers");

  const [runStatus, statusBloc] = useStateMachine(
    () => new ToolStatusBloc(jobHistoryRepository, JOB_TYPES),
  );

  useEffect(() => {
    statusBloc.load();
  }, [statusBloc]);

  const openTool = useMemo<ToolDefinition | null>(
    () => TOOLS.find((t) => t.id === openToolId) ?? null,
    [openToolId],
  );

  if (!isDeveloper) return <DevelopersOnlyCard what="The tools console" />;

  return (
    <div>
      <h1 className="text-foreground mb-6 text-2xl font-bold">Tools</h1>

      <section className="mb-8">
        <SectionHeading
          title="One-time tools"
          hint="Act immediately and leave no run history."
        />
        <div className="bg-card rounded-lg border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Tool</TableHead>
                <TableHead className="w-8" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {ONE_TIME_TOOLS.map((tool) => (
                <TableRow
                  key={tool.id}
                  onClick={() => setOpenToolId(tool.id)}
                  className="hover:bg-muted cursor-pointer"
                >
                  <TableCell className="align-top">
                    <div className="text-foreground font-semibold">
                      {tool.title}
                    </div>
                    <p className="text-muted-foreground mt-1 max-w-2xl text-sm whitespace-normal">
                      {tool.description}
                    </p>
                  </TableCell>
                  <Chevron />
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </section>

      <section>
        <SectionHeading
          title="Job tools"
          hint="Run in the background. Open one to see its history and start a run."
        />
        <div className="bg-card rounded-lg border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Tool</TableHead>
                <TableHead className="w-48">Last run</TableHead>
                <TableHead className="w-8" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {JOB_TOOLS.map((tool) => (
                <TableRow
                  key={tool.id}
                  onClick={() => navigate(toolHistoryPath(tool.id))}
                  className="hover:bg-muted cursor-pointer"
                >
                  <TableCell className="align-top">
                    <div className="text-foreground font-semibold">
                      {tool.title}
                    </div>
                    <p className="text-muted-foreground mt-1 max-w-2xl text-sm whitespace-normal">
                      {tool.description}
                    </p>
                  </TableCell>
                  <TableCell className="align-top">
                    <LastRun
                      latest={runStatus.latest[tool.jobType] ?? null}
                      loaded={runStatus.status === "loaded"}
                    />
                  </TableCell>
                  <Chevron />
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </section>

      {openTool && openTool.kind === "oneClick" && (
        <RunOneClickToolSheet
          title={openTool.title}
          description={openTool.description}
          run={openTool.run}
          onClose={() => setOpenToolId(null)}
        />
      )}
      {openTool && openTool.kind === "custom" && (
        <openTool.Sheet onClose={() => setOpenToolId(null)} />
      )}
    </div>
  );
}
